/**
 * Recommendations Component
 * Horizontal swipeable carousel displaying all menu item recommendations as rounded square images
 */

import { useEffect, useRef, useState } from 'react';
import type { Recommendation } from '../../../types/agenticChat';
import type { Language } from './VoiceOrderingContainer';
import { getItemName } from './recommendationHelpers';

interface RecommendationsProps {
  recommendations: Recommendation[];
  onItemClick: (index: number) => void;
  dismissedIndices: Set<number>;
  language: Language;
  skipFirst?: number; // Skip first N recommendations (default: 0)
  disabled?: boolean; // Disable interactivity when AI is processing
}

export function Recommendations({
  recommendations,
  onItemClick,
  dismissedIndices,
  language,
  skipFirst = 0,
  disabled = false,
}: RecommendationsProps) {
  const [visibleIndices, setVisibleIndices] = useState<number[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Skip first N items and filter out dismissed recommendations
  const filteredRecommendations = recommendations.slice(skipFirst);
  const activeRecommendations = filteredRecommendations.filter(
    (_, displayIndex) => {
      const originalIndex = skipFirst + displayIndex;
      return !dismissedIndices.has(originalIndex);
    }
  );

  // Staggered pop-in animation
  useEffect(() => {
    if (activeRecommendations.length === 0) {
      setVisibleIndices([]);
      return;
    }

    // Reset and animate in all items
    setVisibleIndices([]);
    const timeouts: NodeJS.Timeout[] = [];

    activeRecommendations.forEach((_, index) => {
      const timeout = setTimeout(() => {
        setVisibleIndices((prev) => [...prev, index]);
      }, index * 100); // 100ms delay between each
      timeouts.push(timeout);
    });

    return () => {
      timeouts.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRecommendations.length]);

  // Handle mouse/touch drag for scrolling
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current || disabled) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!scrollContainerRef.current || disabled) return;
    setIsDragging(true);
    setStartX(e.touches[0].pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  if (activeRecommendations.length === 0 || visibleIndices.length === 0) {
    return null;
  }

  return (
    <div
      className="w-full flex justify-center px-4 py-1"
      style={{ 
        zIndex: 50,
        pointerEvents: 'none',
      }}
    >
      {/* Horizontal Scrollable Carousel */}
      <div
        ref={scrollContainerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="flex gap-4 overflow-x-auto px-2"
        style={{
          scrollBehavior: 'smooth',
          scrollbarWidth: 'none',
          cursor: disabled ? 'not-allowed' : (isDragging ? 'grabbing' : 'grab'),
          WebkitOverflowScrolling: 'touch',
          maxWidth: '100%',
          pointerEvents: disabled ? 'none' : 'auto', // Disable pointer events when processing
          opacity: disabled ? 0.5 : 1, // Visual feedback when disabled
        }}
      >
        {filteredRecommendations.map((item, displayIndex) => {
          const originalIndex = skipFirst + displayIndex;
          
          // Skip dismissed items
          if (dismissedIndices.has(originalIndex)) {
            return null;
          }

          const activeDisplayIndex = filteredRecommendations
            .slice(0, displayIndex)
            .filter((_, idx) => {
              const origIdx = skipFirst + idx;
              return !dismissedIndices.has(origIdx);
            }).length;
          const isVisible = visibleIndices.includes(activeDisplayIndex);
          const itemName = getItemName(item.item, language);

          return (
            <div
              key={originalIndex}
              className="relative flex-shrink-0 flex flex-col items-center"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'scale(1)' : 'scale(0.8)',
                transition: 'opacity 400ms ease-out, transform 400ms ease-out',
                width: '70px', // Fixed width for mobile-first
              }}
            >
              {/* Circle with Image */}
              <div
                className={disabled ? 'cursor-not-allowed mb-2' : 'cursor-pointer mb-2'}
                onClick={() => !isDragging && !disabled && onItemClick(originalIndex)}
                onMouseDown={(e) => {
                  // Prevent scroll when clicking item or if disabled
                  if (!isDragging && !disabled) {
                    e.stopPropagation();
                  } else if (disabled) {
                    e.preventDefault();
                  }
                }}
              >
                <div
                  className={`relative rounded-xl border-2 border-white shadow-md transition-all duration-300 ${disabled ? '' : 'hover:scale-105 active:scale-95'}`}
                  style={{
                    width: '70px', // Mobile-first: 70px square
                    height: '70px',
                  }}
                >
                  {/* Image */}
                  {item.img_path ? (
                    <img
                      src={item.img_path}
                      alt={itemName}
                      className="h-full w-full rounded-xl object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          parent.innerHTML =
                            '<div style="display: flex; align-items: center; justify-content: center; height: 100%; width: 100%; font-size: 1.5rem; background: var(--cream-medium); border-radius: 0.75rem;">🍽️</div>';
                        }
                      }}
                    />
                  ) : (
                    <div
                      className="flex h-full w-full items-center justify-center rounded-xl text-2xl"
                      style={{ background: 'var(--cream-medium)' }}
                    >
                      🍽️
                    </div>
                  )}
                </div>
              </div>

              {/* Title Below Circle */}
              <p
                className="text-xs font-medium text-center line-clamp-1"
                style={{
                  color: 'var(--text-primary)',
                  width: '100%',
                }}
                title={itemName}
              >
                {itemName}
              </p>
            </div>
          );
        })}
      </div>

      <style>{`
        .flex.gap-4.overflow-x-auto::-webkit-scrollbar {
          display: none;
        }
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
